import './MatchListByGroupsMobile.scss';

import { ArrowLeft, ArrowRight, Edit } from '@mui/icons-material';
import { Skeleton } from '@mui/lab';
import { Button, Divider, IconButton, Pagination } from '@mui/material';
import { MatchGroup } from 'domain/event/match/MatchGroup';
import {
  MatchParticipant,
  UnpyMatchParticipantsFilters,
} from 'domain/event/match/MatchParticipant';
import { toMatchLigueModel, UnpyMatchFilters } from 'domain/event/match/UnpyMatch';
import { SystemPointsEnum } from 'domain/event/SystemPoints.enum';
import { formatDate } from 'helper/date.helper';
import { SkeletonDatagrid } from 'primary/Components/SkeletonNoContent/SkeletonDatagrid';
import { SkeletonNoContent } from 'primary/Components/SkeletonNoContent/SkeletonNoContent';
import { ParticipantEditRowMobile } from 'primary/events/components/matchs/edit/mobile/ParticipantEditRowMobile';
import { ModalEditDetailsMatchBR } from 'primary/events/components/matchs/edit/ModalEditDetailsMatchBR';
import { HeaderMatchsEdit } from 'primary/events/components/matchs/HeaderMatchsEdit';
import { useBooleanToggle } from 'primary/hooks/useBooleanToggle';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useFetchWithFilterPage } from 'primary/hooks/useFetchWithFilterPage';
import { useTranslate } from 'primary/hooks/useTranslate';
import React, { useEffect, useState } from 'react';

type ParticipantListByMatchProps = {
  eventId?: number;
  filters?: UnpyMatchFilters;
  systemPoints: SystemPointsEnum;
  asView?: boolean;
};
export const ParticipantListByMatchsMobile = ({
  eventId,
  filters,
  systemPoints,
  asView,
}: ParticipantListByMatchProps) => {
  const t = useTranslate();
  const { matchsRepository } = useContextDependency();
  const [selectedGroup, setSelectedGroup] = React.useState<MatchGroup | undefined>();
  const {
    setFilters,
    activeFilters,
    fetchedResource,
    nextPage,
    loading,
    goToPage,
    retry,
    addToCurrentPage,
  } = useFetchWithFilterPage<MatchGroup, UnpyMatchFilters>({
    keyStoreFilters: 'groups-ligue-filters',
    staticFilters: {
      idEvent: eventId,
      withMatchs: true,
    },
    filters: {},
    callApi: (filters, page) => matchsRepository.searchGroup(filters, page, 10),
  });

  useEffect(() => {
    if (fetchedResource?.content && !selectedGroup) {
      setSelectedGroup(fetchedResource.content[0]);
    }
  }, [fetchedResource]);

  useEffect(() => {
    setFilters({ ...(activeFilters?.filters ?? {}), ...filters });
  }, [filters]);

  const currentIndex = fetchedResource?.content?.findIndex(
    (g) => g.id === selectedGroup?.id,
  );
  return (
    <div className={'matchListByGroupsMobile'}>
      <div className={'-groupsSelector'}>
        <SkeletonNoContent displayContent={!loading}>
          <IconButton
            onClick={(e) => {
              if (!currentIndex) return;
              const newGroup = fetchedResource?.content?.[currentIndex - 1];
              setSelectedGroup(newGroup);
            }}
            disabled={!currentIndex}
            className={'-groupsSelector__arrow -left'}
          >
            <ArrowLeft />
          </IconButton>
          {!loading && !fetchedResource?.content && (
            <p>{t('event.groupsSelector.noGroup')}</p>
          )}
          {!loading && selectedGroup && (
            <div className={'-groupSelected'}>
              <span className={'-groupName'}>{selectedGroup.title}</span>
              <span className={'-groupMatchsNumber'}>
                {selectedGroup.matchs[0]?.startDate
                  ? formatDate(selectedGroup.matchs[0]?.startDate, 'readable(with hour)')
                  : '--/--/----'}
              </span>
            </div>
          )}
          <IconButton
            onClick={(e) => {
              if (
                fetchedResource?.content?.length &&
                currentIndex === fetchedResource?.content?.length - 1
              ) {
                return;
              }

              const newGroup = fetchedResource?.content?.[(currentIndex ?? 0) + 1];
              setSelectedGroup(newGroup);
            }}
            disabled={
              !fetchedResource?.content?.length ||
              currentIndex === fetchedResource?.content?.length - 1
            }
            className={'-groupsSelector__arrow -right'}
          >
            <ArrowRight />
          </IconButton>
        </SkeletonNoContent>
      </div>
      <div className={'matchListByGroupsMobile__matchList'}>
        {!selectedGroup && <Skeleton animation="wave" height={'100%'} width="100%" />}
        {selectedGroup && (
          <ParticipantsListOfMatchsMobile
            asView={asView}
            group={selectedGroup}
            systemPoint={systemPoints as SystemPointsEnum}
          />
        )}
      </div>
    </div>
  );
};

type ParticipantListOfMatchMpbileProps = {
  group: MatchGroup;
  systemPoint: SystemPointsEnum;
  filters?: UnpyMatchFilters;
  asView?: boolean;
};

const ParticipantsListOfMatchsMobile = ({
  group,
  filters,
  systemPoint,
  asView,
}: ParticipantListOfMatchMpbileProps) => {
  const { matchsRepository } = useContextDependency();

  const {
    setFilters,
    activeFilters,
    fetchedResource,
    nextPage,
    retry,
    goToPage,
    loading: loadingPage,
    addToCurrentPage,
  } = useFetchWithFilterPage<MatchParticipant, UnpyMatchParticipantsFilters>({
    keyStoreFilters: 'matchs-ligue-br-filters',
    staticFilters: {
      matchId: group.matchs[0].id,
    },
    filters: {},
    callApi: (filters, page, nbPerPage) =>
      matchsRepository.searchParticipants(filters, page, nbPerPage),
  });

  const t = useTranslate();

  const [loading, setLoading] = useState(false);
  const [isOpenEditScore, , openEditScore, closeEditScore] = useBooleanToggle();
  const setFilterByKey = (
    key: keyof UnpyMatchParticipantsFilters,
    val?: string | number | boolean,
  ) => {
    setFilters({ ...(activeFilters?.filters ?? {}), [key]: val });
  };

  useEffect(() => {
    setFilters({
      ...(activeFilters?.filters ?? {}),
      ...(filters ?? {}),
    });
  }, [group, filters]);
  return (
    <div className={'matchListOfGroup'}>
      <div className={'matchListOfGroup__header'}>
        <div className={'-titleWithDetails'}>
          <h4>{t('event.matchs.participantMatchTitle')}</h4>
          {!asView && (
            <Button variant={'contained'} color={'primary'} onClick={openEditScore}>
              <Edit />
              {t('event.matchs.openEditScoreBtn')}
            </Button>
          )}
        </div>
      </div>
      <HeaderMatchsEdit
        retry={retry}
        withTextField
        event={group.event}
        filters={activeFilters?.filters ?? {}}
        // @ts-ignore
        setFilters={setFilterByKey}
      />

      <div className={'matchListOfGroup-content --scrollBarContent'}>
        <Divider />
        {!loadingPage && !fetchedResource?.content?.length && (
          <p>{t('event.matchs.noMatchsInGroup')}</p>
        )}
        <div className={'matchListOfGroup-pagination'}>
          <Pagination
            key={fetchedResource?.currentPage}
            count={fetchedResource?.pagesCount || 1}
            page={fetchedResource?.currentPage ? fetchedResource?.currentPage + 1 : 1}
            size={'medium'}
            onChange={(e, value) => goToPage(value - 1)}
          />
        </div>
        {loadingPage && <SkeletonDatagrid rowNumber={10} />}
        {!loadingPage &&
          fetchedResource?.content?.length &&
          fetchedResource?.content.map((participant) => (
            <ParticipantEditRowMobile
              withoutDate
              asView={asView}
              key={participant.id}
              match={toMatchLigueModel(group.matchs[0])}
              participant={participant}
              systemPoint={group.event.pointSystem}
              onEdited={retry}
              typeEvent={group.event.typeEvent.code}
            />
          ))}
        <ModalEditDetailsMatchBR
          isOpen={isOpenEditScore}
          handleClose={closeEditScore}
          systemPoint={systemPoint}
          onValidEdit={retry}
          match={group.matchs[0]}
          retry={retry}
        />
      </div>
    </div>
  );
};

const LignesSkeleton = () => {
  return (
    <>
      {Array(5)
        .fill(0)
        .map((val, index) => (
          <div style={{ width: '100%', height: '60px' }} key={index}>
            <Skeleton animation="wave" height={'100%'} width="100%" />
          </div>
        ))}
    </>
  );
};
